import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { PermissionsForm } from './PermissionsForm';
import { eventTeamApi } from '../../api/eventTeam';
import type { EventManager, EventManagerPermissions } from '../../types';
import styles from './EditManagerModal.module.css';

interface EditManagerModalProps {
  eventId: string;
  manager: EventManager | null;
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export function EditManagerModal({ eventId, manager, isOpen, onClose, onChanged }: EditManagerModalProps) {
  const [permissions, setPermissions] = useState<EventManagerPermissions | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  useEffect(() => {
    if (manager) {
      setPermissions({ ...manager.permissions });
      setConfirmingRemove(false);
    }
  }, [manager]);

  if (!manager || !permissions) {
    return null;
  }

  const displayName =
    (manager.userId && typeof manager.userId === 'object' && manager.userId.name) || manager.email;

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await eventTeamApi.update(eventId, manager._id, permissions);
      toast.success('Permissions updated');
      onChanged();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error ?? 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!confirmingRemove) {
      setConfirmingRemove(true);
      return;
    }
    setRemoving(true);
    try {
      await eventTeamApi.remove(eventId, manager._id);
      toast.success(`${displayName} removed from the team`);
      onChanged();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error ?? 'Failed to remove manager');
    } finally {
      setRemoving(false);
    }
  };

  const busy = submitting || removing;

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title={`Edit ${displayName}`} size="md">
      <div className={styles['form']}>
        <div className={styles['scroll-body']}>
        <div className={styles['identity']}>
          <span className={styles['identity-email']}>{manager.email}</span>
          <span className={styles['identity-status']} data-status={manager.status}>
            {manager.status === 'pending' ? 'Invitation pending' : 'Active'}
          </span>
        </div>

        <PermissionsForm value={permissions} onChange={setPermissions} disabled={busy} />
        </div>

        <div className={styles['actions']}>
          <Button
            type="button"
            variant={confirmingRemove ? 'danger' : 'ghost'}
            onClick={handleRemove}
            loading={removing}
            disabled={submitting}
          >
            {confirmingRemove ? 'Confirm remove' : 'Remove from team'}
          </Button>
          <div className={styles['actions-right']}>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSave} loading={submitting} disabled={removing}>
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
